import React, { useState, useEffect } from 'react';
import { Modal, Button, Badge, Spinner } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { serverUrlAuth } from '../../../serverUrl';
import FurnitureNavbar from '../../../components/layout/Nav';
import './updates.scss';
import withAuth from '../../auth/WithAuth';
import { IoClose } from 'react-icons/io5';
import { FaEnvelope } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const ClientProjectUpdates = () => {
  const navigate = useNavigate();
  
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isMarkingRead, setIsMarkingRead] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await serverUrlAuth.get('/projects/client-project-updates/');
      setProjects(res.data);
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  };

  // Mark messages as read when opening chat
  const markMessagesAsRead = async (projectId) => {
    if (!projectId) return;

    setIsMarkingRead(true);
    try {
      await serverUrlAuth.post(`/updates/messages/${projectId}/mark-read/`);
      // Refresh projects to update unread count
      await fetchProjects();
    } catch (err) {
      console.error('Error marking messages as read:', err);
    } finally {
      setIsMarkingRead(false);
    }
  };

  // Handle opening messages page
  const handleOpenMessages = async (project) => {
    // Mark messages as read before opening
    await markMessagesAsRead(project.id);
    
    // Navigate to messages page
    navigate('/messages');
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Helper function to get progress color based on percentage
  const getProgressColor = (progress) => {
    if (progress < 30) return '#ff6b6b'; // Red
    if (progress < 70) return '#673200'; // Brown/Orange
    return '#6bcf7f'; // Green
  };

  return (
    <>
      <FurnitureNavbar />

      <div className="client-updates-page">
        <section className="updates-hero">
          <div className="hero-overlay">
            <motion.div
              className="hero-content"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1>📢 Project Updates</h1>
              <p>
                Stay informed about your project progress with real-time updates
                from artisans
              </p>
              <div className="hero-stats">
                <div className="stat">
                  <span className="stat-number">{projects.length}</span>
                  <span className="stat-label">Total Projects</span>
                </div>
                <div className="stat">
                  <span className="stat-number">
                    {projects.reduce(
                      (total, project) =>
                        total + (project.unread_updates_count || 0),
                      0
                    )}
                  </span>
                  <span className="stat-label">Unread Messages</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <div className="container-main">
          {projects.length === 0 ? (
            <motion.div
              className="empty-state"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="empty-icon">📋</div>
              <h3>No projects with updates yet</h3>
              <p>When you have project updates, they'll appear here.</p>
            </motion.div>
          ) : (
            <div className="projects-grid">
              <AnimatePresence>
                {projects.map((project, index) => (
                  <motion.div
                    key={project.id}
                    className="project-card"
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.95 }}
                    transition={{
                      duration: 0.4,
                      delay: index * 0.1,
                      type: 'spring',
                      stiffness: 100,
                    }}
                    whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  >
                    <div className="card-image-container">
                      {project.images?.length > 0 ? (
                        <img
                          src={project.images[0].image}
                          alt={project.title}
                          className="card-image"
                        />
                      ) : (
                        <div className="no-image-placeholder">
                          <div className="placeholder-icon">🛠️</div>
                          <span>No Image</span>
                        </div>
                      )}
                      <div className="card-overlay">
                        <div className="status-badge">
                          <span
                            className={`status-dot ${project.status}`}
                          ></span>
                          {project.status?.replace('-', ' ') || 'Unknown'}
                        </div>
                        {project.unread_updates_count > 0 && (
                          <Badge className="unread-badge pulse">
                            {project.unread_updates_count} new
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="card-content">
                      <h3 className="card-title">{project.title}</h3>

                      <div className="card-meta">
                        <div className="meta-item">
                          <span className="meta-label">Agreed Cost</span>
                          <span className="meta-value">
                            KSh{' '}
                            {parseFloat(project.agreed_cost || 0).toLocaleString()}
                          </span>
                        </div>

                        <div className="meta-item">
                          <span className="meta-label">Progress</span>
                          <span className="meta-value">
                            {project.progress || 0}%
                          </span>
                        </div>

                        <div className="meta-item">
                          <span className="meta-label">Last Message</span>
                          <span className="meta-value">
                            {project.last_update_time
                              ? formatTime(project.last_update_time)
                              : 'No messages yet'}
                          </span>
                        </div>
                      </div>

                      {/* Progress Bar Section */}
                      <div className="progress-section">
                        <div className="progress-header">
                          <span>Project Progress</span>
                          <span className="progress-percent">
                            {project.progress || 0}%
                          </span>
                        </div>
                        <div className="custom-progress">
                          <div
                            className="progress-bar"
                            style={{
                              width: `${project.progress || 0}%`,
                              backgroundColor: getProgressColor(
                                project.progress || 0
                              ),
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="card-actions">
                      <button 
                        className="view-updates-btn"
                        onClick={() => handleOpenMessages(project)}
                        disabled={isMarkingRead}
                      >
                        <FaEnvelope className="me-1" />
                        {project.unread_updates_count > 0 
                          ? `Messages (${project.unread_updates_count})` 
                          : 'Messages'}
                      </button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Image Viewer Modal */}
        <Modal
          show={!!selectedImage}
          onHide={() => setSelectedImage(null)}
          centered
          size="xl"
          className="image-viewer-modal"
          fullscreen="md-down"
        >
          <Modal.Body className="image-viewer-body p-0">
            {selectedImage && (
              <>
                <Button
                  variant="link"
                  className="viewer-close"
                  onClick={() => setSelectedImage(null)}
                >
                  <IoClose />
                </Button>
                <motion.img
                  key={selectedImage}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  src={selectedImage}
                  alt="Selected update"
                  className="viewer-image"
                />
                <div className="image-info">
                  <span className="image-index">Project Update Image</span>
                  <span className="image-size">Click outside to close</span>
                </div>
              </>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};

export default withAuth(ClientProjectUpdates, 'client');